import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleHelp, ClipboardList, ContactRound } from 'lucide-react';
import { Screen } from '@/app/layout/Screen';
import { Badge, MiniIcon, Panel, Row, RowList } from '@/components/ui/Surface';
import { plural } from '@/components/ui/Profile';
import { useAccountIdentity } from '@/store/accountIdentity';
import { useDoctorProfilesStore } from '@/store/doctorProfilesStore';

/**
 * Экран 22. Профиль: аккаунт и профессиональные профили.
 *
 * Показывается только то, что известно от сервера. Раздел не выдумывает ни
 * имени, ни статистики: бэкенд отдаёт почту, список профилей и активный
 * профиль, и этим состав экрана исчерпывается.
 *
 * Программы курса здесь нет намеренно: у неё своя вкладка внизу, и вторая
 * дорога к тому же экрану только запутывает. Выхода из аккаунта нет по той же
 * причине: вход привязан к аккаунту мессенджера, и «выйти» означало бы лишь
 * повторный вход тем же человеком через секунду.
 */
export function ProfileScreen() {
  const navigate = useNavigate();
  const identity = useAccountIdentity();

  const profiles = useDoctorProfilesStore((state) => state.profiles);
  const activeId = useDoctorProfilesStore((state) => state.activeId);
  const load = useDoctorProfilesStore((state) => state.load);

  useEffect(() => {
    void load();
  }, [load]);

  const activeProfile = profiles.find((profile) => profile.doctor_profile_id === activeId);

  const menu = [
    {
      icon: <ContactRound className="h-[17px] w-[17px]" aria-hidden />,
      title: 'Профессиональные профили',
      subtitle: activeProfile ? `Активный: ${activeProfile.profile_name}` : 'Выберите или создайте профиль',
      trailing: <Badge>{profiles.length}</Badge>,
      to: '/profiles',
    },
    {
      icon: <ClipboardList className="h-[17px] w-[17px]" aria-hidden />,
      title: 'Анкета профиля',
      subtitle: 'Ответы, по которым работает ассистент',
      to: '/profile/survey',
    },
    {
      icon: <CircleHelp className="h-[17px] w-[17px]" aria-hidden />,
      title: 'Помощь и поддержка',
      subtitle: 'Ответы на вопросы и связь с командой',
      to: '/help',
    },
  ];

  return (
    <Screen subtitle="Профиль" tab="profile">
      <Panel className="mb-2.5">
        <Row
          icon={
            <span
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-lime text-[15px] font-medium text-black"
              aria-hidden
            >
              {identity.initials}
            </span>
          }
          title={<span className="text-[15px]">{identity.title}</span>}
          subtitle={`${profiles.length} ${plural(profiles.length, 'профиль', 'профиля', 'профилей')}`}
        />
      </Panel>

      <RowList>
        {menu.map((item) => (
          <div key={item.to}>
            <Row
              icon={<MiniIcon tone="lime">{item.icon}</MiniIcon>}
              title={item.title}
              subtitle={item.subtitle}
              trailing={item.trailing}
              chevron={!item.trailing}
              onClick={() => navigate(item.to)}
            />
          </div>
        ))}
      </RowList>
    </Screen>
  );
}
